/**
 * Shared 3D option button — web port of iOS
 * `client-native/src/features/lesson/components/ActivityButton.tsx`
 * (a `Button3D` + `ShakeAnimation` with a state color map).
 *
 * Used by MCQ / Match / Fill. States: default | selected | incorrect | matched.
 * Imperative `triggerCorrect` / `triggerIncorrect` flash green/red (+ shake +
 * `navigator.vibrate(30)` where available), matching the iOS Medium haptic.
 *
 * Colors come exclusively from `@/ui` tokens (never hardcoded):
 *   green #34A853 · blue #0c92f1 · red #c72d25 · white #fff (lesson) /
 *   greyLightest #f5f5f5 (feed).
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { colors, fontFamily } from "@/ui";
import type { ActivityVariant } from "../types";

export type ActivityButtonState =
  | "default"
  | "selected"
  | "incorrect"
  | "matched";

export interface ActivityButtonRef {
  triggerCorrect: () => void;
  triggerIncorrect: () => void;
}

export interface ActivityButtonProps {
  text: string;
  onPress?: () => void;
  disabled?: boolean;
  state?: ActivityButtonState;
  variant?: ActivityVariant;
  /** Smaller text (Match buttons hold longer phrases). */
  textVariant?: "base" | "small";
  /** Hold the correct flash this long (ms). `null` = stay green (lesson). */
  correctAnimationHoldMs?: number | null;
  /** Hold the incorrect flash this long (ms) before reverting. */
  incorrectAnimationHoldMs?: number;
  className?: string;
}

const ANIMATION_DURATION = 500;
const DEFAULT_INCORRECT_HOLD_MS = 500;

/**
 * Inject the shake keyframes once. `App.css` is read-only (foundation owns it),
 * so the kit ships its own scoped keyframes idempotently.
 */
const SHAKE_STYLE_ID = "activity-kit-shake-keyframes";
function ensureShakeKeyframes(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(SHAKE_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = SHAKE_STYLE_ID;
  style.textContent =
    "@keyframes activity-shake{0%,100%{transform:translateX(0)}" +
    "20%{transform:translateX(-6px)}40%{transform:translateX(6px)}" +
    "60%{transform:translateX(-4px)}80%{transform:translateX(4px)}}";
  document.head.appendChild(style);
}

function getCorrectClearDelay(hold: number | null | undefined): number | null {
  return hold === undefined ? ANIMATION_DURATION : hold;
}

/** Best-effort haptic — no-op where the API is missing (desktop / iOS Safari). */
function vibrate(ms: number): void {
  try {
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      navigator.vibrate(ms);
    }
  } catch {
    /* ignore — vibration is a nicety, never a hard requirement */
  }
}

const ActivityButton = forwardRef<ActivityButtonRef, ActivityButtonProps>(
  function ActivityButton(
    {
      text,
      onPress,
      disabled = false,
      state = "default",
      variant = "lesson",
      textVariant = "base",
      correctAnimationHoldMs,
      incorrectAnimationHoldMs = DEFAULT_INCORRECT_HOLD_MS,
      className,
    },
    ref,
  ) {
    const [animState, setAnimState] = useState<
      "default" | "correct" | "incorrect"
    >("default");
    const [shaking, setShaking] = useState(false);
    const correctTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const incorrectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const shakeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimers = () => {
      if (correctTimer.current) clearTimeout(correctTimer.current);
      if (incorrectTimer.current) clearTimeout(incorrectTimer.current);
      correctTimer.current = null;
      incorrectTimer.current = null;
    };

    const triggerCorrect = useCallback(() => {
      clearTimers();
      setAnimState("correct");
      const delay = getCorrectClearDelay(correctAnimationHoldMs);
      if (delay === null) return; // stay green (lesson CorrectDrawer flow)
      correctTimer.current = setTimeout(() => {
        setAnimState("default");
        correctTimer.current = null;
      }, delay);
    }, [correctAnimationHoldMs]);

    const triggerIncorrect = useCallback(() => {
      clearTimers();
      setAnimState("incorrect");
      vibrate(30);
      setShaking(true);
      if (shakeTimer.current) clearTimeout(shakeTimer.current);
      shakeTimer.current = setTimeout(() => setShaking(false), 400);
      incorrectTimer.current = setTimeout(() => {
        setAnimState("default");
        incorrectTimer.current = null;
      }, incorrectAnimationHoldMs);
    }, [incorrectAnimationHoldMs]);

    useEffect(() => {
      ensureShakeKeyframes();
      return () => {
        clearTimers();
        if (shakeTimer.current) clearTimeout(shakeTimer.current);
      };
    }, []);

    useImperativeHandle(ref, () => ({ triggerCorrect, triggerIncorrect }), [
      triggerCorrect,
      triggerIncorrect,
    ]);

    // Resolve face color from animation > matched/selected > state > default.
    const { background, color } = resolveColors(animState, state, variant);
    const isGrayedOut = disabled && state !== "matched";

    // iOS Button3D face: a subtle top→bottom gradient (~5% darker at the
    // bottom), a soft 1px inner edge ring that hugs the rounded rect (iOS's
    // stacked low-opacity edge strokes), a top white gloss highlight, and a soft
    // drop shadow ALL AROUND the button (iOS look) — NOT the hard Duolingo
    // "0 4px 0" bottom-only bar.
    const faceBottom = darkenHex(background, 0.05);
    const edgeColor = darkenHex(background, 0.11);
    const lightFace = isLightHex(background);
    const highlightOpacity = lightFace ? 0.5 : 0.07;
    const boxShadow = [
      `inset 0 1px 0 rgba(255,255,255,${highlightOpacity})`,
      `inset 0 0 0 1px ${edgeColor}`,
      "0 2px 8px rgba(0,0,0,0.12)",
      "0 1px 2px rgba(0,0,0,0.08)",
    ].join(", ");

    return (
      <button
        type="button"
        onClick={onPress}
        disabled={disabled}
        aria-pressed={state === "selected"}
        className={className}
        style={{
          // 3D face
          minWidth: 80,
          width: "100%",
          padding: textVariant === "small" ? "12px 14px" : "16px 18px",
          borderRadius: 16,
          border: "none",
          background: `linear-gradient(180deg, ${background} 0%, ${faceBottom} 100%)`,
          color,
          fontFamily,
          fontWeight: 700,
          fontSize: textVariant === "small" ? "0.875rem" : "1rem",
          lineHeight: 1.2,
          cursor: disabled ? "default" : "pointer",
          boxShadow,
          transition:
            "background-color 120ms ease, transform 90ms ease, box-shadow 90ms ease",
          opacity: isGrayedOut ? 0.45 : 1,
          // hyphenate long words so they never overflow the pill
          overflowWrap: "anywhere",
          hyphens: "auto",
          animation: shaking ? "activity-shake 0.4s ease" : undefined,
          WebkitTapHighlightColor: "transparent",
          touchAction: "manipulation",
        }}
      >
        {text}
      </button>
    );
  },
);

function hexToRgb(hex: string): [number, number, number] {
  let h = hex.trim().replace("#", "");
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  if (h.length !== 6) return [255, 255, 255];
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function darkenHex(hex: string, ratio: number): string {
  const [r, g, b] = hexToRgb(hex);
  const f = (c: number) =>
    Math.max(0, Math.min(255, Math.round(c * (1 - ratio))))
      .toString(16)
      .padStart(2, "0");
  return `#${f(r)}${f(g)}${f(b)}`;
}

function isLightHex(hex: string): boolean {
  const [r, g, b] = hexToRgb(hex);
  return r > 220 && g > 220 && b > 220;
}

function resolveColors(
  animState: "default" | "correct" | "incorrect",
  state: ActivityButtonState,
  variant: ActivityVariant,
): { background: string; color: string } {
  if (animState === "correct") return { background: colors.green, color: colors.white };
  if (animState === "incorrect") return { background: colors.red, color: colors.white };
  if (state === "matched") return { background: colors.green, color: colors.white };
  if (state === "selected") return { background: colors.blue, color: colors.white };
  if (state === "incorrect") return { background: colors.red, color: colors.white };
  return {
    background: variant === "feed" ? colors.greyLightest : colors.white,
    color: colors.primary,
  };
}

export default ActivityButton;
